import TrendingAdsIcon from "@/assets/icons/TrendingAd.svg";
import TwitterIcon from "@/assets/icons/Twitter.svg";
import ArrowDown from "@/assets/icons/ArrowDownSm.svg";
import GrowthIcon from "@/assets/icons/GrowthGreen.svg";
import HashTag from "@/assets/icons/HashTag.svg";
import Instagram from "@/assets/icons/InstagramSm.svg";
import { useGetAllTrendsQuery } from "@/redux/feature/trends/trendsApi";
import ProductCard from "../ProductCard";
import { useState } from "react";
import Modal from "@/components/ui/Modal";
import ReactCountryFlag from "react-country-flag";

export type TrendVideo = {
  id: string;
  brand: string;
  product: string;
  country: string;
  video_url: string;
  platform: string;
  caption: string;
};

export type Trend = {
  trend: string;
  engagement: string;
  description: string;
  videos: TrendVideo[];
};

type TrendsProps = {
  brand: string;
  product: string;
  country: string;
};

// dropdown options
const platformOptions = [
  { value: "twitter", label: "X (Twitter)", icon: <TwitterIcon /> },
  { value: "instagram", label: "Instagram", icon: <Instagram /> },
];

const regionOptions = [
  {
    value: "india",
    label: "India",
    icon: (
      <ReactCountryFlag
        countryCode="IN"
        svg
        style={{ width: "22px", height: "15px", borderRadius: "4px" }}
      />
    ),
  },
  {
    value: "usa",
    label: "USA",
    icon: (
      <ReactCountryFlag
        countryCode="US"
        svg
        style={{ width: "22px", height: "15px", borderRadius: "4px" }}
      />
    ),
  },
];

const industryOptions = [
  { value: "d2c", label: "D2C / E-commerce" },
  { value: "tech", label: "Tech" },
  { value: "fashion", label: "Fashion" },
];

const Trends = ({ brand, product, country }: TrendsProps) => {
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [showAllTrendVideos, setShowAllTrendVideos] = useState(false);

  // filter states
  const [platform, setPlatform] = useState(platformOptions[0]);
  const [region, setRegion] = useState(regionOptions[0]);
  const [industry, setIndustry] = useState(industryOptions[0]);

  const { data } = useGetAllTrendsQuery({
    brand,
    product,
    country,
    industry: industry.value,
    platform: platform.value,
    region: region.value,
  });

  const hasResults = data?.results && data.results.length > 0;

  return (
    <section className="border border-[#E4E7EC] rounded-xl px-4 py-4 my-7">
      {/* header */}
      <div className="flex gap-x-4">
        <TrendingAdsIcon />
        <div>
          <h3 className="text-[#0D0D0D] text-xl font-medium font-grotesk">
            Trending Videos (Beta)
          </h3>
          <p className="text-[#6B6B6B] text-xs mt-2 font-grotesk">
            Discover high-view content across YouTube, X (Twitter), and Google
            Trends. Filter by hashtags and industries to spot repeatable
            patterns—then turn them into tested ad frameworks.
          </p>
        </div>
      </div>

      {/* filters */}
      <div className="mt-8 flex gap-x-2.5 items-center">
        {/* Platform */}
        <Dropdown
          label="Platform"
          selected={platform}
          options={platformOptions}
          onSelect={setPlatform}
        />
        {/* Region */}
        <Dropdown
          label="Region"
          selected={region}
          options={regionOptions}
          onSelect={setRegion}
        />
        {/* Industry */}
        <Dropdown
          label="Industry"
          selected={industry}
          options={industryOptions}
          onSelect={setIndustry}
        />
      </div>

      {/* Content */}
      {hasResults ? (
        /* trend list */
        data?.results?.map((trend: Trend, index: number) => (
          <div
            key={index}
            className="mt-8 border border-[#CCCCCC] px-3 py-4 rounded-lg"
          >
            <div className="flex gap-x-3 flex-wrap">
              <h3 className="text-[#0D0D0D] text-xl font-semibold">
                {trend?.trend}
              </h3>
              <div className="border border-[#E5E5E5] bg-[#E5E5E5] py-1 px-2 rounded-lg flex items-center">
                <p className="text-xs tracking-normal">USA</p>
              </div>
              <div className="border border-[#E5E5E5] bg-[#E5E5E5] py-1 px-2 rounded-lg flex items-center">
                <HashTag />
                <p className="text-xs tracking-normal">RunnerClub</p>
              </div>
              <div className="border border-[#E5E5E5] bg-[#E5E5E5] py-1 px-2 rounded-lg flex items-center">
                <Instagram />
                <p className="text-xs tracking-normal">Instagram</p>
              </div>
            </div>

            <p className="text-[#A7A7A7] text-sm font-medium mt-4">
              {trend.description}
            </p>

            <div className="mt-6">
              <h3 className="text-lg text-gray-500 font-medium mb-2">
                Trend Videos
              </h3>
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
                {trend?.videos?.slice(0, 3).map((video) => (
                  <ProductCard
                    key={video.id}
                    video={video as any}
                    currentlyPlaying={currentlyPlaying}
                    setCurrentlyPlaying={setCurrentlyPlaying}
                  />
                ))}
              </div>
              <div className="text-center">
                <button
                  onClick={() => setShowAllTrendVideos(true)}
                  className="text-[#344054] text-xs font-medium border border-[#E4E7EC] px-2 py-1 rounded-lg mt-5 hover:bg-gray-50"
                >
                  View All Videos
                </button>
              </div>
            </div>

            <Modal
              width="900px"
              setOpen={setShowAllTrendVideos}
              isOpen={showAllTrendVideos}
            >
              <ShowAllTrendVideos
                videos={trend.videos}
                currentlyPlaying={currentlyPlaying}
                setCurrentlyPlaying={setCurrentlyPlaying}
              />
            </Modal>
          </div>
        ))
      ) : (
        <NoTrendsFound filters={{ platform, region, industry }} />
      )}
    </section>
  );
};

export default Trends;

/* Dropdown Component */
const Dropdown = ({
  label,
  selected,
  options,
  onSelect,
}: {
  label: string;
  selected: { value: string; label: string; icon?: React.ReactNode };
  options: { value: string; label: string; icon?: React.ReactNode }[];
  onSelect: (option: any) => void;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="py-[7px] px-3 border border-[#00000033] flex gap-x-2 rounded-lg items-center"
      >
        <p className="text-[#7D7D7D]">{label}</p>
        {selected.icon && selected.icon}
        <p>{selected.label}</p>
        <ArrowDown />
      </button>
      {open && (
        <div className="absolute z-20 bg-white border rounded-lg mt-1 w-44 shadow-md">
          {options.map((opt) => (
            <div
              key={opt.value}
              onClick={() => {
                onSelect(opt);
                setOpen(false);
              }}
              className="px-3 py-2 hover:bg-gray-100 flex items-center gap-x-2 cursor-pointer"
            >
              {opt.icon && opt.icon}
              <p>{opt.label}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* Modal Content */
const ShowAllTrendVideos = ({
  videos,
  currentlyPlaying,
  setCurrentlyPlaying,
}: {
  videos: TrendVideo[];
  currentlyPlaying: string | null;
  setCurrentlyPlaying: React.Dispatch<React.SetStateAction<string | null>>;
}) => {
  return (
    <div>
      <h3 className="text-lg text-gray-500 font-medium mb-2">
        All Trends Videos
      </h3>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
        {videos?.map((video) => (
          <ProductCard
            key={video.id}
            video={video as any}
            currentlyPlaying={currentlyPlaying}
            setCurrentlyPlaying={setCurrentlyPlaying}
          />
        ))}
      </div>
    </div>
  );
};

const NoTrendsFound = ({ filters }: { filters: any }) => (
  <div className="text-center py-12">
    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <svg
        className="w-8 h-8 text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
    </div>
    <h3 className="text-lg font-medium text-gray-900 mb-2">No Trends Found</h3>
    <p className="text-gray-500 text-sm max-w-md mx-auto mb-6">
      We couldn't find any trending videos matching your current filters. Try
      adjusting your search criteria or check back later for new trends.
    </p>
    <div className="flex flex-wrap justify-center gap-2 mb-4">
      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
        Platform: {filters.platform.label}
      </span>
      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
        Region: {filters.region.label}
      </span>
      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
        Industry: {filters.industry.label}
      </span>
    </div>
  </div>
);
